import { useState } from "react";
import { Boxes, Download, GitBranch, ScrollText, SlidersHorizontal, Target } from "lucide-react";
import GlowCard from "../components/GlowCard";
import StatusBadge from "../components/StatusBadge";

/**
 * AI Governance — the controls behind every AI-assisted finding.
 *
 * NOTHING ON THIS PAGE IS WIRED. It is the prototype's layout rebuilt in the live shell so the shape
 * of the screen can be judged before the plumbing exists: which model produced a finding, which
 * prompt version, what it was measured against and who signed it off. Every value below is fixed
 * sample content, and the page says so at the top rather than leaving a reader to work it out from a
 * date that never changes.
 *
 * What it will read from, when it is built: the model registry wants its own container, prompt
 * versions belong with the extractor version already stamped on every rule, and the regression
 * figures come from replaying a held-out set of adjudicated findings.
 */

interface RegistryEntry {
  model: string;
  role: string;
  version: string;
  approved: string;
  owner: string;
  guardrails: string;
  state: "Production" | "Evaluation";
}

const REGISTRY: RegistryEntry[] = [
  {
    model: "google/gemini-2.5-flash",
    role: "AI Compliance Engine — clause interpretation",
    version: "engine-v0.2",
    approved: "2026-06-04",
    owner: "Head of AI Assurance",
    guardrails: "Grounding required · evidence quote mandatory · temp 0.2",
    state: "Production",
  },
  {
    model: "deterministic-rules/v3.2",
    role: "Rule Engine — mandatory clause & format checks",
    version: "ruleset-3.2.1",
    approved: "2026-06-02",
    owner: "Lead Discipline Engineer",
    guardrails: "No inference · 100% reproducible · unit-tested",
    state: "Production",
  },
  {
    model: "google/gemini-2.5-pro",
    role: "Candidate — complex multi-sheet reasoning",
    version: "candidate",
    approved: "—",
    owner: "Head of AI Assurance",
    guardrails: "Shadow mode only · no findings issued to reviewers",
    state: "Evaluation",
  },
];

interface PromptVersion {
  id: string;
  version: string;
  summary: string;
  at: string;
  by: string;
  approved: boolean;
}

const PROMPTS: PromptVersion[] = [
  {
    id: "PR-COMPLIANCE-CHECK",
    version: "v4.1",
    summary: "Tightened grounding instruction — every finding must cite a verbatim quote and page.",
    at: "2026-06-28",
    by: "Head of AI Assurance",
    approved: true,
  },
  {
    id: "PR-DOC-PARSE",
    version: "v2.7",
    summary: "Added title-block and revision-history extraction for IFC drawing sets.",
    at: "2026-06-19",
    by: "Platform Engineering",
    approved: true,
  },
  {
    id: "PR-SEVERITY-ROUTE",
    version: "v1.9",
    summary: "Confidence threshold aligned to 75% manual-review gate.",
    at: "2026-06-06",
    by: "Lead Discipline Engineer",
    approved: true,
  },
  {
    id: "PR-COMPLIANCE-CHECK",
    version: "v4.2-draft",
    summary: "Proposed HF-service material compatibility heuristics (API RP 751).",
    at: "2026-06-01",
    by: "Head of AI Assurance",
    approved: false,
  },
];

interface Metric {
  name: string;
  target: string;
  value: string;
  /** Where the bar sits, 0-100. Presentational only — the number beside it is the claim. */
  fill: number;
  tone: "pass" | "warn";
}

const METRICS: Metric[] = [
  { name: "Precision on golden set", target: "target ≥ 0.90", value: "0.94", fill: 94, tone: "pass" },
  { name: "Recall on golden set", target: "target ≥ 0.85", value: "0.91", fill: 91, tone: "pass" },
  { name: "Grounding rate (evidence cited)", target: "target 100%", value: "100%", fill: 100, tone: "pass" },
  { name: "Hallucinated clause references", target: "target 0", value: "0", fill: 100, tone: "pass" },
  { name: "Mean confidence drift vs. baseline", target: "target < 5%", value: "+0.8%", fill: 16, tone: "pass" },
];

const CONTROLS = [
  {
    regulation: "EU AI Act (Reg. 2024/1689)",
    scope: "High-risk AI — human oversight, logging, technical documentation",
    control: "Mandatory human approval on Critical/Major; append-only audit trail; model registry below.",
  },
  {
    regulation: "ISO/IEC 42001",
    scope: "AI management system — lifecycle, roles, change control",
    control: "Named model owners, versioned prompts, documented approval before promotion to production.",
  },
  {
    regulation: "NIST AI RMF",
    scope: "Measure & Manage — validity, reliability, drift monitoring",
    control: "Golden-dataset regression run on every model or prompt change; drift tracked against baseline.",
  },
];

const Governance = () => {
  /**
   * The gate moves, and nothing downstream reads it.
   *
   * Left interactive on purpose: the question this page exists to answer is whether a governance
   * screen belongs in the product at all, and a slider that does not move cannot be judged. It
   * resets on navigation, which is the honest behaviour for a control with nothing behind it.
   */
  const [gate, setGate] = useState(75);
  /** The "what this covers" note. Closed by default — it is an explanation, not a heading. */
  const [helpOpen, setHelpOpen] = useState(false);

  return (
    <div className="vr-page">
      <div className="vr-page-head">
        <div>
          {/* Worth reading once, then in the way. Same "i" toggle the other headings use. */}
          <h1 className="vr-h-page">
            AI Governance
            <button
              type="button"
              className="vr-info-btn"
              aria-expanded={helpOpen}
              title={helpOpen ? "Hide" : "What this covers"}
              onClick={() => setHelpOpen((open) => !open)}
            >
              i
            </button>
          </h1>
          <p hidden={!helpOpen}>
            Model registry, prompt version control, golden-dataset regression and change approval —
            the controls behind every AI-assisted finding.
          </p>
        </div>
        <div className="vr-head-actions">
          <span className="vr-chip">Sample content</span>
          <button className="vr-btn vr-btn-outline" disabled title="Not built yet">
            <Download size={14} /> Export Governance Pack
          </button>
        </div>
      </div>

      <div className="vr-grid vr-grid-4">
        <GlowCard className="vr-stat" hover={false}>
          <p className="vr-stat-value vr-det">3</p>
          <p className="vr-stat-label">Registered models</p>
        </GlowCard>
        <GlowCard className="vr-stat" hover={false}>
          <p className="vr-stat-value vr-det">4</p>
          <p className="vr-stat-label">Prompt versions under control</p>
        </GlowCard>
        <GlowCard className="vr-stat" hover={false}>
          <p className="vr-stat-value vr-done-num">100%</p>
          <p className="vr-stat-label">Grounding rate on live findings</p>
        </GlowCard>
        <GlowCard className="vr-stat" hover={false}>
          <p className="vr-stat-value vr-major">5</p>
          <p className="vr-stat-label">Findings routed to manual review</p>
        </GlowCard>
      </div>

      <GlowCard hover={false}>
        <div className="vr-card-head">
          <Boxes size={14} />
          <h3>Model Registry</h3>
        </div>
        <div className="vr-gov-list">
          {REGISTRY.map((entry) => (
            <div className="vr-gov-entry" key={entry.model}>
              <div className="vr-gov-entry-top">
                <div>
                  <p className="vr-gov-model">{entry.model}</p>
                  <p className="vr-gov-role">{entry.role}</p>
                </div>
                <StatusBadge
                  status={entry.state === "Production" ? "pass" : "info"}
                  label={entry.state}
                />
              </div>
              <div className="vr-gov-fields">
                <div className="vr-gov-field">
                  <span className="vr-gov-key">Version</span>
                  <span className="vr-cell-code">{entry.version}</span>
                </div>
                <div className="vr-gov-field">
                  <span className="vr-gov-key">Approved</span>
                  <span className="vr-cell-code">{entry.approved}</span>
                </div>
                <div className="vr-gov-field">
                  <span className="vr-gov-key">Accountable owner</span>
                  <span className="vr-gov-val">{entry.owner}</span>
                </div>
                <div className="vr-gov-field">
                  <span className="vr-gov-key">Guardrails</span>
                  <span className="vr-gov-val">{entry.guardrails}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </GlowCard>

      <div className="vr-grid vr-grid-2">
        <GlowCard hover={false}>
          <div className="vr-card-head">
            <GitBranch size={14} />
            <h3>Prompt Version Control</h3>
          </div>
          <div className="vr-gov-list">
            {PROMPTS.map((prompt) => (
              <div className="vr-gov-entry" key={`${prompt.id}-${prompt.version}`}>
                <div className="vr-gov-entry-top">
                  <span className="vr-gov-prompt-id">
                    {prompt.id} · {prompt.version}
                  </span>
                  <StatusBadge
                    status={prompt.approved ? "pass" : "warning"}
                    label={prompt.approved ? "Approved" : "Pending approval"}
                  />
                </div>
                <p className="vr-gov-summary">{prompt.summary}</p>
                <p className="vr-gov-byline">
                  {prompt.at} · {prompt.by}
                </p>
              </div>
            ))}
          </div>
        </GlowCard>

        <div className="vr-gov-col">
          <GlowCard hover={false}>
            <div className="vr-card-head">
              <Target size={14} />
              <h3>Golden-Dataset Regression</h3>
            </div>
            <p className="vr-muted vr-gov-lead">
              Run automatically before any model or prompt is promoted. Last run 2026-06-04 against
              240 adjudicated findings.
            </p>
            <div className="vr-meters">
              {METRICS.map((metric) => (
                <div className="vr-meter" key={metric.name}>
                  <div className="vr-meter-head">
                    <span className="vr-meter-name">{metric.name}</span>
                    <span className="vr-gov-target">{metric.target}</span>
                    <span className={`vr-meter-num vr-gov-${metric.tone}`}>{metric.value}</span>
                  </div>
                  <span className="vr-meter-track">
                    <span
                      className={`vr-meter-fill vr-gov-fill-${metric.tone}`}
                      style={{ width: `${metric.fill}%` }}
                    />
                  </span>
                </div>
              ))}
            </div>
          </GlowCard>

          <GlowCard hover={false}>
            <div className="vr-card-head">
              <SlidersHorizontal size={14} />
              <h3>Confidence Gate</h3>
            </div>
            <p className="vr-muted vr-gov-lead">
              AI findings below this confidence are never auto-issued — they are queued for
              discipline-engineer verification.
            </p>
            <div className="vr-gov-gate">
              <input
                type="range"
                min={50}
                max={100}
                value={gate}
                onChange={(e) => setGate(Number(e.target.value))}
                className="vr-gov-slider"
                aria-label="Confidence gate"
              />
              <span className="vr-gov-gate-num">{gate}%</span>
            </div>
            <p className="vr-foot-note">
              Changing this value is a governed action — it is logged to the audit trail and requires
              AI Assurance approval before taking effect in production.
            </p>
          </GlowCard>
        </div>
      </div>

      <GlowCard hover={false}>
        <div className="vr-card-head">
          <ScrollText size={14} />
          <h3>Regulatory Control Mapping</h3>
        </div>
        <div className="vr-grid vr-grid-3">
          {CONTROLS.map((control) => (
            <div className="vr-gov-control" key={control.regulation}>
              <p className="vr-gov-reg">{control.regulation}</p>
              <p className="vr-gov-scope">{control.scope}</p>
              <p className="vr-gov-val">{control.control}</p>
            </div>
          ))}
        </div>
      </GlowCard>
    </div>
  );
};

export default Governance;
